import api from './api';
import { ApiResponse, PageResponse } from '../types/api';

// Definindo a interface PageRequest que está faltando
export interface PageRequest {
  page?: number;
  size?: number;
  sort?: string;
}

// Itens para criação de venda (request)
export interface VendaItemRequestDTO {
  produtoId: string | number;
  quantidade: number;
  precoUnitario: number;
  desconto?: number;
  observacoes?: string;
}

export interface VendaRequestDTO {
  // Campos opcionais do cliente
  nomeCliente?: string;
  documentoCliente?: string;
  emailCliente?: string;
  telefoneCliente?: string;
  enderecoCliente?: string;
  // Data da venda (obrigatória, formato date: YYYY-MM-DD)
  dataVenda: string;
  vendedorId?: string | number;
  clienteId?: string;
  observacoes?: string;
  desconto?: number;
  // Itens obrigatórios
  itens: VendaItemRequestDTO[];
}

export interface VendaResponseDTO {
  id: string;
  clienteId?: string;
  clienteNome?: string;
  // Compatível com novas respostas da API
  nomeCliente?: string;
  vendedorId?: string;
  vendedorNome?: string;
  dataVenda?: string;
  // Compatível com novas respostas da API
  dataCriacao?: string;
  itens: VendaItemDTO[];
  pagamentos: VendaPagamentoDTO[];
  valorTotal: number;
  status: 'PENDENTE' | 'FINALIZADA' | 'CANCELADA' | 'EM_PROCESSAMENTO' | 'ENTREGUE' | 'PARCIALMENTE_ENTREGUE';
  observacoes?: string;
  criadoEm: string;
  atualizadoEm: string;
}

export interface VendaItemDTO {
  produtoId: string;
  produtoNome?: string;
  produtoCodigoBarras?: string;
  unidade?: string;
  quantidade: number;
  precoUnitario: number;
  valorTotal: number;
}

export interface VendaPagamentoDTO {
  formaPagamento: 'DINHEIRO' | 'CARTAO_CREDITO' | 'CARTAO_DEBITO' | 'PIX' | 'BOLETO';
  valor: number;
  observacoes?: string;
}

export interface EstoqueResponseDTO {
  produtoId: string;
  produtoNome?: string;
  produtoCodigo?: string;
  quantidade: number;
}

const vendaService = {
  listar: async (params?: PageRequest & { clienteId?: string; status?: string; dataVendaInicio?: string; dataVendaFim?: string }) => {
    return api.get<PageResponse<VendaResponseDTO>>('/v1/vendas', { params });
  },

  listarPorCliente: async (clienteId: string, params?: PageRequest & { tipoPagamentoId?: string }) => {
    const sp = new URLSearchParams();
    if (params?.page !== undefined) sp.append('page', String(params.page));
    if (params?.size !== undefined) sp.append('size', String(params.size));
    if (params?.sort) sp.append('sort', params.sort);
    if (params?.tipoPagamentoId) sp.append('tipoPagamentoId', String(params.tipoPagamentoId));
    const qs = sp.toString();
    const url = qs ? `/v1/vendas/cliente/${encodeURIComponent(clienteId)}?${qs}` : `/v1/vendas/cliente/${encodeURIComponent(clienteId)}`;
    return api.get<ApiResponse<any>>(url);
  },

  // Listagem de vendas por período, conforme API: /v1/vendas/periodo
  // Envia "dataInicial" e "dataFinal" como parâmetros de data (YYYY-MM-DD)
  // Aceita também dataVendaInicio/dataVendaFim e mapeia para os nomes esperados pela API
  listarPorPeriodo: async (params?: PageRequest & { clienteId?: string; status?: string; dataVendaInicio?: string; dataVendaFim?: string; dataInicial?: string; dataFinal?: string }) => {
    const { dataVendaInicio, dataVendaFim, dataInicial, dataFinal, ...rest } = params || {};
    const start = dataInicial ?? dataVendaInicio;
    const end = dataFinal ?? dataVendaFim;
    const query = {
      ...rest,
      dataInicial: start,
      dataFinal: end,
    } as any;
    return api.get<PageResponse<VendaResponseDTO>>('/v1/vendas/periodo', { params: query });
  },

  obter: async (id: string) => {
    return api.get<VendaResponseDTO>(`/v1/vendas/${id}`);
  },

  // Obtém a última venda PENDENTE para um vendedor específico
  obterUltimaPendentePorVendedor: async (vendedorId: string | number) => {
    return api.get<VendaResponseDTO>(`/v1/vendas/ultima/pendente/vendedor/${vendedorId}`);
  },

  criar: async (venda: VendaRequestDTO) => {
    return api.post<VendaResponseDTO>('/v1/vendas', venda);
  },

  atualizar: async (id: string, venda: VendaRequestDTO) => {
    return api.put<VendaResponseDTO>(`/v1/vendas/${id}`, venda);
  },

  // Atualiza apenas o cliente da venda
  atualizarCliente: async (id: string, clienteId: string) => {
    return api.put<VendaResponseDTO>(`/v1/vendas/${id}/cliente`, { clienteId });
  },

  cancelar: async (id: string, motivo?: string) => {
    const qs = motivo ? `?motivo=${encodeURIComponent(motivo)}` : '';
    return api.patch<VendaResponseDTO>(`/v1/vendas/${id}/cancelar${qs}`);
  },

  finalizar: async (id: string) => {
    // Alinhar com a documentação: PATCH sem body, apenas id no path
    return api.patch<VendaResponseDTO>(`/v1/vendas/${id}/finalizar`);
  },

  registrarPagamento: async (id: string, pagamentos: VendaPagamentoDTO[]) => {
    return api.post<VendaResponseDTO>(`/v1/vendas/${id}/pagamentos`, { pagamentos });
  },

  consultarEstoque: async (produtoId: string) => {
    return api.get<EstoqueResponseDTO>(`/v1/estoque/produto/${produtoId}`);
  },

  // === DASHBOARD VENDAS ===
  // GET /v1/vendas/vendas-por-dia-semana?dataInicial=YYYY-MM-DD&dataFinal=YYYY-MM-DD
  vendasPorDiaSemana: async (params?: { dataInicial?: string; dataFinal?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.dataInicial) searchParams.append('dataInicial', params.dataInicial);
    if (params?.dataFinal) searchParams.append('dataFinal', params.dataFinal);
    const qs = searchParams.toString();
    const url = qs ? `/v1/vendas/vendas-por-dia-semana?${qs}` : '/v1/vendas/vendas-por-dia-semana';
    return api.get<any>(url);
  },

  // GET /v1/vendas/vendas-por-mes?ano=YYYY
  // Algumas versões da API exigem explicitamente o parâmetro "ano".
  // Derivamos o ano a partir de dataFinal ou dataInicial quando não for informado.
  vendasPorMes: async (params?: { ano?: number; dataInicial?: string; dataFinal?: string }) => {
    let ano = params?.ano;
    if (!ano) {
      const source = params?.dataFinal || params?.dataInicial;
      ano = source ? new Date(source).getFullYear() : new Date().getFullYear();
    }
    const searchParams = new URLSearchParams();
    searchParams.append('ano', String(ano));
    const url = `/v1/vendas/vendas-por-mes?${searchParams.toString()}`;
    return api.get<any>(url);
  },

  // GET /v1/vendas/dashboard?dataInicial=YYYY-MM-DD&dataFinal=YYYY-MM-DD
  // Retorna um mapa de indicadores para compor cards do dashboard
  obterDashboard: async (params?: { dataInicial?: string; dataFinal?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.dataInicial) searchParams.append('dataInicial', params.dataInicial);
    if (params?.dataFinal) searchParams.append('dataFinal', params.dataFinal);
    const qs = searchParams.toString();
    const url = qs ? `/v1/vendas/dashboard?${qs}` : '/v1/vendas/dashboard';
    return api.get<any>(url);
  },

  // GET /v1/vendas/produtos-mais-vendidos?dataInicial=YYYY-MM-DD&dataFinal=YYYY-MM-DD&limit=n
  produtosMaisVendidos: async (params?: { dataInicial?: string; dataFinal?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.dataInicial) searchParams.append('dataInicial', params.dataInicial);
    if (params?.dataFinal) searchParams.append('dataFinal', params.dataFinal);
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));
    const qs = searchParams.toString();
    const url = qs ? `/v1/vendas/produtos-mais-vendidos?${qs}` : '/v1/vendas/produtos-mais-vendidos';
    return api.get<any>(url);
  }
  ,

  // === RELATÓRIO DE VENDAS ===
  // GET /v1/vendas/relatorio
  // Params: dataInicial (YYYY-MM-DD), dataFinal (YYYY-MM-DD), tiposPagamentoIds (array[string]), vendedorId (string), status (string)
  relatorioVendas: async (params: { dataInicial: string; dataFinal: string; tiposPagamentoIds?: string[]; vendedorId?: string; status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.dataInicial) searchParams.append('dataInicial', params.dataInicial);
    if (params?.dataFinal) searchParams.append('dataFinal', params.dataFinal);
    if (Array.isArray(params?.tiposPagamentoIds)) {
      params!.tiposPagamentoIds!.forEach((id) => {
        if (id) searchParams.append('tiposPagamentoIds', String(id));
      });
    }
    if (params?.vendedorId) searchParams.append('vendedorId', String(params.vendedorId));
    if (params?.status) searchParams.append('status', String(params.status));
    const qs = searchParams.toString();
    const url = qs ? `/v1/vendas/relatorio?${qs}` : '/v1/vendas/relatorio';
    return api.get<any>(url);
  }
};

export default vendaService;
